// ===========================================
// Email sequence — "does the form differ from the saved record?"
// ===========================================
//
// ⚠️ ITS OWN MODULE, not a reuse of `isCustomEmailDirty`. That function compares
// a flat campaign with targeting arrays; a sequence is a header plus an ORDERED
// LIST of steps, so the only shared idea is `richHtmlEqual`. Bending one
// function to cover both shapes would make each harder to read and neither
// safe.

import { richHtmlEqual } from '../../utils/richHtmlCompare';
import type { EmailSequence, EmailSequenceAnchor } from '../../types/emailSequence.types';

/** One step as the editor holds it, before it is sent. */
export interface SequenceStepFormState {
  /**
   * `offsetDays` is `number | ''` because it is bound to a text input — an
   * emptied field is `''`, which must not read as day 0.
   */
  offsetDays: number | '';
  subject: string;
  preheader: string;
  content: string;
}

export interface EmailSequenceFormState {
  name: string;
  description: string;
  anchor: EmailSequenceAnchor;
  sendHour: number;
  isActive: boolean;
  steps: SequenceStepFormState[];
}

/** The subset of the saved record this comparison reads. */
export type SavedEmailSequence = Pick<
  EmailSequence,
  'name' | 'description' | 'anchor' | 'sendHour' | 'isActive' | 'steps'
>;

/**
 * Does the form differ from the SAVED record?
 *
 * A NEW sequence is dirty as soon as it carries anything worth losing — there
 * is no saved record to compare against, so "has the admin typed something"
 * is the only sensible question.
 */
export function isSequenceDirty(
  form: EmailSequenceFormState,
  saved: SavedEmailSequence | undefined,
  isNew: boolean
): boolean {
  if (isNew) {
    return (
      form.name.trim() !== '' ||
      form.description.trim() !== '' ||
      form.steps.some(
        (s) => s.subject.trim() !== '' || s.preheader.trim() !== '' || !richHtmlEqual(s.content, '')
      )
    );
  }

  if (!saved) return false;

  if (
    form.name !== saved.name ||
    // An emptied description is `''`; the saved value is `null`. They are the
    // same state and must not read as an edit.
    form.description !== (saved.description ?? '') ||
    form.anchor !== saved.anchor ||
    form.sendHour !== saved.sendHour ||
    form.isActive !== saved.isActive
  ) {
    return true;
  }

  // ⚠️ ORDER IS MEANINGFUL HERE, unlike the custom-email targeting sets.
  // Positions are reassigned by array index on save, so a reorder IS an edit.
  if (form.steps.length !== saved.steps.length) return true;

  return form.steps.some((step, i) => {
    const savedStep = saved.steps[i];
    if (!savedStep) return true;
    return (
      step.offsetDays !== savedStep.offsetDays ||
      step.subject !== savedStep.subject ||
      step.preheader !== (savedStep.preheader ?? '') ||
      // ⚠️ `richHtmlEqual`, never `!==`. Quill rewrites equivalent markup on
      // load (attribute order, `<p><br></p>` for an empty line), so a string
      // compare reports every untouched sequence as dirty and the guard cries
      // wolf until people click through it without reading.
      !richHtmlEqual(step.content, savedStep.content)
    );
  });
}
